import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CircularProgress } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { formRules, maxLengthRules } from 'src/constants/formConstants';
import { changePassword } from 'src/store/actions/authActions';
import {
  useErrorSelector,
  useLoadingSelector,
} from 'src/utils/useLoadingSelector';

import { removeModal } from '../../../features/globalSlice';
import { Modal } from '../../../store/models/Modal';
import Button from '../Button/Button';
import { FormInput } from '../FormInput/FormInput';
import classes from './ChangePasswordModal.module.scss';

export interface ChangePasswordModalProps {
  modal?: Modal;
}

export interface ChangePasswordFormData {
  current: string;
  password: string;
  repeatPassword: string;
}

const defaultValues: ChangePasswordFormData = {
  current: '',
  password: '',
  repeatPassword: '',
};

const ChangePasswordModal: FC<ChangePasswordModalProps> = ({ modal }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [error, setError] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const loading = useLoadingSelector({
    actionType: changePassword.toString(),
  });

  const changeError = useErrorSelector<ChangePasswordFormData>({
    actionType: changePassword.toString(),
  });

  const { control, handleSubmit, formState, watch } =
    useForm<ChangePasswordFormData>({
      mode: 'onChange',
      defaultValues,
    });

  useEffect(() => {
    const subscription = watch(() => setError(false));
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit: SubmitHandler<ChangePasswordFormData> = (formData) => {
    if (formData.password !== formData.repeatPassword) {
      setError(true);
      return;
    }
    dispatch(changePassword(formData));
  };

  return (
    <div className={classes.Container}>
      {!loading ? (
        <>
          <h4>{modal.title}</h4>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <FormInput<ChangePasswordFormData>
              label={t('settings:current_password')}
              className={classes.Input}
              control={control}
              name="current"
              type={showCurrentPassword ? 'text' : 'password'}
              inputProps={{
                fullWidth: true,
                inputProps: { autoComplete: 'new-password' },
              }}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!changeError?.errorMessage}
              helperText={changeError?.errorMessage}
              endAdornment={
                <FontAwesomeIcon
                  icon={showCurrentPassword ? faEye : faEyeSlash}
                  onClick={() => setShowCurrentPassword((prev) => !prev)}
                />
              }
            />
            <FormInput<ChangePasswordFormData>
              label={t('login:new_password')}
              className={classes.Input}
              control={control}
              name="password"
              type={showNewPassword ? 'text' : 'password'}
              inputProps={{
                fullWidth: true,
                inputProps: { autoComplete: 'new-password' },
              }}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!changeError?.password}
              helperText={changeError?.password}
              endAdornment={
                <FontAwesomeIcon
                  icon={showNewPassword ? faEye : faEyeSlash}
                  onClick={() => setShowNewPassword((prev) => !prev)}
                />
              }
            />
            <FormInput<ChangePasswordFormData>
              label={t('login:repeat_new_password')}
              className={classes.Input}
              control={control}
              name="repeatPassword"
              type={showRepeatPassword ? 'text' : 'password'}
              inputProps={{
                fullWidth: true,
                inputProps: { autoComplete: 'new-password' },
              }}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={error}
              helperText={error && t('login:passwords_dont_match')}
              endAdornment={
                <FontAwesomeIcon
                  icon={showRepeatPassword ? faEye : faEyeSlash}
                  onClick={() => setShowRepeatPassword((prev) => !prev)}
                />
              }
            />

            <div className={classes.ButtonGroup}>
              {modal?.primaryButtonText ? (
                <Button
                  className={classes.Primary}
                  type="submit"
                  disabled={!formState?.isValid}
                >
                  {modal?.primaryButtonText}
                </Button>
              ) : null}
              {modal?.secondaryButtonText ? (
                <Button
                  className={classes.Secondary}
                  onClick={() => dispatch(removeModal())}
                  variant="outlined"
                >
                  {modal?.secondaryButtonText}
                </Button>
              ) : null}
            </div>
          </form>
        </>
      ) : (
        <div className={classes.Loading}>
          <CircularProgress color="primary" size="3rem" />
        </div>
      )}
    </div>
  );
};

export default ChangePasswordModal;
